import React from 'react';

import { useRouter } from 'next/router';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { useAuth0 } from '@auth0/auth0-react';

import { generateDynamicHeaders, privateHttpLink, useUser } from '@itp/talent';

import { useImpersonate } from '../hooks/useImpersonate';
import { ensureValidToken, getLocalToken, isTokenExpired, useLocalAuth } from './localAuthContext';

export const ApolloClientProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const {
    getAccessTokenSilently,
    isAuthenticated: isAuth0Authenticated,
    loginWithRedirect,
  } = useAuth0();
  const { refreshAccessToken } = useLocalAuth();
  const { impersonate } = useImpersonate();
  const { user } = useUser();
  const { domain, selectedCompany } = generateDynamicHeaders(router.pathname, user);

  const errorLink = React.useMemo(
    () =>
      onError(({ forward, graphQLErrors, networkError, operation }) => {
        const isUnauthenticated =
          graphQLErrors?.some(
            (e) =>
              e.extensions?.code === 'UNAUTHENTICATED' ||
              e.message?.includes('timestamp check failed') ||
              e.message?.includes('Token has been revoked'),
          ) ||
          (networkError &&
            'statusCode' in networkError &&
            (networkError as any).statusCode === 401);

        if (isUnauthenticated && getLocalToken()) {
          return new Observable((observer) => {
            refreshAccessToken()
              .then((newToken) => {
                if (!newToken) {
                  observer.error(new Error('Session expired'));
                  return;
                }
                const oldHeaders = operation.getContext().headers;
                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: `Bearer ${newToken}`,
                  },
                });
                const subscriber = {
                  complete: observer.complete.bind(observer),
                  error: observer.error.bind(observer),
                  next: observer.next.bind(observer),
                };
                forward(operation).subscribe(subscriber);
              })
              .catch((err) => {
                observer.error(err);
              });
          });
        }
      }),
    [refreshAccessToken],
  );

  const authLink = React.useMemo(
    () =>
      setContext(async (_, { headers, ...context }) => {
        let token: null | string = null;

        if (!context.bypassAuth) {
          const syncToken = getLocalToken();

          if (syncToken) {
            if (isTokenExpired(syncToken)) {
              token = await ensureValidToken();
            } else {
              token = syncToken;
            }
          } else if (isAuth0Authenticated) {
            try {
              token = await getAccessTokenSilently();
            } catch (e: any) {
              if (
                e.error === 'consent_required' ||
                e.error === 'missing_refresh_token' ||
                e.error === 'invalid_grant' ||
                e.message?.includes('refresh_token') ||
                e.description?.includes('refresh_token') ||
                e.error_description?.includes('refresh_token')
              ) {
                if (typeof window !== 'undefined') {
                  loginWithRedirect();
                }
              } else {
                console.error('Non-refresh token auth error:', e);
              }
            }
          }
        }

        return {
          headers: {
            ...headers,
            ...(token ? { authorization: `Bearer ${token}` } : {}),
            ...(impersonate?.externalId && { 'x-impersonate-as': impersonate.externalId }),
            ...(selectedCompany && { 'tp-company': selectedCompany }),
            ...(domain && { 'tp-domain': domain }),
          },
          ...context,
        };
      }),
    [
      domain,
      getAccessTokenSilently,
      isAuth0Authenticated,
      loginWithRedirect,
      impersonate?.externalId,
      selectedCompany,
    ],
  );

  const client = React.useMemo(
    () =>
      new ApolloClient({
        cache: new InMemoryCache(),
        link: ApolloLink.from([errorLink, authLink, privateHttpLink]),
      }),
    [errorLink, authLink],
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
